import React, { useState, useEffect } from 'react';
import { affiliateService } from '../../services/affiliateService';
import toast from 'react-hot-toast';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  PieChart,
  Pie,
  Cell
} from 'recharts';

interface PerformanceMetric {
  date: string;
  clicks: number;
  impressions: number;
  conversions: number;
  conversionRate: number;
  revenue: number;
  commissions: number;
}

interface TopAffiliate {
  id: string;
  name: string;
  conversions: number;
  revenue: number;
  commissions: number;
  conversionRate: number;
}

// Sample data for the placeholder
const samplePerformanceData: PerformanceMetric[] = [
  { date: '2025-06-01', clicks: 120, impressions: 2500, conversions: 6, conversionRate: 5.00, revenue: 1200, commissions: 120 },
  { date: '2025-06-08', clicks: 145, impressions: 3000, conversions: 8, conversionRate: 5.52, revenue: 1600, commissions: 160 },
  { date: '2025-06-15', clicks: 160, impressions: 3200, conversions: 9, conversionRate: 5.63, revenue: 1800, commissions: 180 },
  { date: '2025-06-22', clicks: 185, impressions: 3700, conversions: 11, conversionRate: 5.95, revenue: 2200, commissions: 220 },
  { date: '2025-06-29', clicks: 210, impressions: 4200, conversions: 14, conversionRate: 6.67, revenue: 2800, commissions: 280 }
];

const sampleTopAffiliates: TopAffiliate[] = [
  { id: '3', name: 'Robert Johnson', conversions: 25, revenue: 5000, commissions: 500, conversionRate: 7.5 },
  { id: '1', name: 'John Doe', conversions: 15, revenue: 3000, commissions: 300, conversionRate: 5.0 },
  { id: '7', name: 'Sarah Williams', conversions: 12, revenue: 2400, commissions: 240, conversionRate: 6.2 },
  { id: '5', name: 'Emma Wilson', conversions: 9, revenue: 1800, commissions: 180, conversionRate: 4.8 },
  { id: '4', name: 'Michael Brown', conversions: 7, revenue: 1400, commissions: 140, conversionRate: 3.9 }
];

const trafficSources = [
  { source: 'Direct', percentage: 35 },
  { source: 'Blog', percentage: 25 },
  { source: 'Social Media', percentage: 20 },
  { source: 'Email', percentage: 15 },
  { source: 'Other', percentage: 5 }
];

export const ReportsAnalytics: React.FC<{ programId?: string }> = ({ programId }) => {
  const [performanceData, setPerformanceData] = useState<PerformanceMetric[]>([]);
  const [topAffiliates, setTopAffiliates] = useState<TopAffiliate[]>([]);
  const [trafficData, setTrafficData] = useState<{source: string, percentage: number}[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('30d');
  const [dateRange, setDateRange] = useState('30');
  const [reportType, setReportType] = useState('performance');

  // Map UI range to API range
  const toApiRange = (r: string) => (r === 'custom' ? '30d' : `${r}d`);

  // Fetch report data via affiliateService
  const fetchAnalyticsData = async (period: string) => {
    setLoading(true);
    try {
      if (!programId) {
        setPerformanceData([]);
        setTopAffiliates([]);
        setTrafficData([]);
        return;
      }
      // Performance timeseries
      await fetchPerformanceMetrics(period);
      // Traffic sources breakdown
      await fetchTrafficSources(period);
      // Top affiliates
      const top = await affiliateService.getTopAffiliates(programId, 5);
      const formattedAffiliates: TopAffiliate[] = (top?.affiliates || []).map((a: any) => ({
        id: a.id,
        name: a.name || a.contact_name || a.company_name || a.email || 'Affiliate',
        conversions: Number(a.total_conversions || 0),
        revenue: Number(a.total_revenue || 0),
        commissions: Number(a.total_commission || 0),
        conversionRate: Number(a.conversion_rate || 0)
      }));
      setTopAffiliates(formattedAffiliates.length ? formattedAffiliates : sampleTopAffiliates);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      toast.error('Failed to load analytics. Showing sample data.');
      // Fall back to sample data for development
      setPerformanceData(samplePerformanceData);
      setTopAffiliates(sampleTopAffiliates);
      setTrafficData(trafficSources);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch performance metrics
  const fetchPerformanceMetrics = async (period: string) => {
    try {
      const range = period.includes('d') ? period : toApiRange(period);
      const res = await affiliateService.getTimeseries(range, programId);
      const series = Array.isArray(res?.series) ? res.series : []; 
      const formatted: PerformanceMetric[] = (series || []).map((m: any) => ({
        date: m.date || m.bucket || m.day,
        clicks: Number(m.clicks || 0),
        impressions: Number(m.impressions || 0),
        conversions: Number(m.conversions || 0),
        conversionRate: Number(m.conversion_rate || m.conv_rate || (Number(m.clicks || 0) ? (Number(m.conversions || 0) / Number(m.clicks || 1)) * 100 : 0)),
        revenue: Number(m.sales || m.revenue || 0),
        commissions: Number(m.commissions || 0)
      }));
      setPerformanceData(formatted);
    } catch (error) {
      console.error('Error fetching performance metrics:', error);
      // Will fall back to sample data in the parent function
      throw error;
    }
  };
  
  // Fetch traffic sources via affiliateService
  const fetchTrafficSources = async (period: string) => {
    try {
      const range = period.includes('d') ? period : toApiRange(period);
      const res = await affiliateService.getSourceBreakdown(range, programId);
      const items = Array.isArray((res as any)?.breakdown) ? (res as any).breakdown : [];
      const total = items.reduce((sum: number, s: any) => sum + Number(s.count || 0), 0) || 0;
      const formatted = items.map((s: any) => ({
        source: s.source || s.utm_source || 'unknown',
        percentage: total ? Number(((Number(s.count || 0) / total) * 100).toFixed(2)) : 0
      }));
      setTrafficData(formatted);
    } catch (error) {
      console.error('Error fetching traffic sources:', error);
      // Will fall back to sample data in the parent function
      throw error;
    }
  };
  
  // Initialize data fetch on component mount
  useEffect(() => {
    fetchAnalyticsData(selectedPeriod);
  }, [selectedPeriod, programId]);
  
  // Handle period change
  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
  };

  const handleDateRangeChange = (range: string) => {
    setDateRange(range);
    fetchPerformanceMetrics(range).catch(() => {
      // swallow, parent shows toast
    });
  };

  // Calculate totals for the performance metrics
  const totals = performanceData.reduce(
    (acc, curr) => ({
      clicks: acc.clicks + curr.clicks,
      impressions: acc.impressions + curr.impressions,
      conversions: acc.conversions + curr.conversions,
      revenue: acc.revenue + curr.revenue,
      commissions: acc.commissions + curr.commissions
    }),
    { clicks: 0, impressions: 0, conversions: 0, revenue: 0, commissions: 0 }
  );

  const avgConversionRate = totals.clicks > 0 
    ? ((totals.conversions / totals.clicks) * 100).toFixed(2) 
    : '0.00';

  // Helper function to format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Affiliate Program Reports</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Analyze your affiliate program performance.
          </p>
        </div>
        
        {/* Report Controls */}
        <div className="px-4 py-5 sm:px-6 flex flex-wrap gap-4 items-center justify-between bg-gray-50 border-t border-b border-gray-200">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center">
              <label htmlFor="date-range" className="mr-2 text-sm font-medium text-gray-700">Date Range:</label>
              <select
                id="date-range"
                name="date-range"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border-gray-300 rounded-md"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="180">Last 180 days</option>
                <option value="365">Last year</option>
                <option value="custom">Custom range</option>
              </select>
            </div>
            
            <div className="flex items-center">
              <label htmlFor="report-type" className="mr-2 text-sm font-medium text-gray-700">Report Type:</label>
              <select
                id="report-type"
                name="report-type"
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border-gray-300 rounded-md"
              >
                <option value="performance">Performance</option>
                <option value="affiliates">Top Affiliates</option>
                <option value="traffic">Traffic Sources</option>
              </select>
            </div>
          </div>
          
          <div>
            <button
              type="button"
              onClick={async () => {
                try {
                  const range = toApiRange(dateRange);
                  const data = await affiliateService.exportConversions(range);
                  const csv = typeof data === 'string' ? data : (data?.csv || '');
                  if (!csv) throw new Error('No data to export');
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `affiliate-conversions-${range}.csv`;
                  link.click();
                  URL.revokeObjectURL(url);
                } catch (e) {
                  console.error(e);
                  toast.error('Failed to export conversions');
                }
              }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="-ml-1 mr-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              Export Report
            </button>
          </div>
        </div>
        
        {/* Key Metrics Summary */}
        <div className="px-4 py-5 sm:p-6 border-b border-gray-200">
          <h4 className="text-base font-medium text-gray-900 mb-4">Key Metrics</h4>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 md:grid-cols-6">
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">Clicks</dt>
              <dd className="mt-1 text-2xl font-semibold text-blue-600">{totals.clicks.toLocaleString()}</dd>
            </div>
            
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">Impressions</dt>
              <dd className="mt-1 text-2xl font-semibold text-blue-600">{totals.impressions.toLocaleString()}</dd>
            </div>
            
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">Conversions</dt>
              <dd className="mt-1 text-2xl font-semibold text-green-600">{totals.conversions}</dd>
            </div>
            
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">Conv. Rate</dt>
              <dd className="mt-1 text-2xl font-semibold text-green-600">{avgConversionRate}%</dd>
            </div>
            
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">Revenue</dt>
              <dd className="mt-1 text-2xl font-semibold text-gray-900">{formatCurrency(totals.revenue)}</dd>
            </div>
            
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">Commissions</dt>
              <dd className="mt-1 text-2xl font-semibold text-orange-500">{formatCurrency(totals.commissions)}</dd>
            </div>
          </dl>
        </div>
        
        {/* Performance Report */}
        {reportType === 'performance' && (
          <div className="px-4 py-5 sm:p-6">
            <h4 className="text-base font-medium text-gray-900 mb-4">Performance Over Time</h4>
            
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <div className="bg-white rounded-lg border border-gray-200 h-72">
                {performanceData && performanceData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={performanceData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={(v) => new Date(v).toLocaleDateString()} />
                      <YAxis />
                      <Tooltip formatter={(value: any, name: any) => [value, name]} labelFormatter={(l) => new Date(l as any).toLocaleDateString()} />
                      <Legend />
                      <Line type="monotone" dataKey="clicks" name="Clicks" stroke="#3b82f6" dot={false} strokeWidth={2} />
                      <Line type="monotone" dataKey="conversions" name="Conversions" stroke="#10b981" dot={false} strokeWidth={2} />
                      <Line type="monotone" dataKey="commissions" name="Commissions" stroke="#f59e0b" dot={false} strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-500">No data for selected period</div>
                )}
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clicks</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Impressions</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conversions</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conv. Rate</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commissions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {performanceData.map((data) => (
                    <tr key={data.date}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(data.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {data.clicks.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {data.impressions.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {data.conversions}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {data.conversionRate.toFixed(2)}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(data.revenue)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatCurrency(data.commissions)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* Top Affiliates Report */}
        {reportType === 'affiliates' && (
          <div className="px-4 py-5 sm:p-6">
            <h4 className="text-base font-medium text-gray-900 mb-4">Top Performing Affiliates</h4>
            
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <div className="bg-white rounded-lg border border-gray-200 h-72 flex items-center justify-center text-gray-500">
                Coming soon: Top Affiliates visualization
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Affiliate</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conversions</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commissions</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conv. Rate</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {topAffiliates.map((affiliate, index) => (
                    <tr key={affiliate.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {affiliate.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {affiliate.conversions}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(affiliate.revenue)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatCurrency(affiliate.commissions)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {affiliate.conversionRate.toFixed(2)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* Traffic Sources Report */}
        {reportType === 'traffic' && (
          <div className="px-4 py-5 sm:p-6">
            <h4 className="text-base font-medium text-gray-900 mb-4">Traffic Sources</h4>
            
            <div className="bg-gray-50 p-4 rounded-lg mb-4 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg border border-gray-200 h-72">
                {trafficData && trafficData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Tooltip formatter={(v: any) => [`${v}%`, 'Share']} />
                      <Legend />
                      <Pie
                        data={trafficData}
                        dataKey="percentage"
                        nameKey="source"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        label={(entry) => `${entry.source}: ${entry.percentage}%`}
                      >
                        {trafficData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={["#3b82f6","#10b981","#f59e0b","#ef4444","#8b5cf6","#14b8a6"][index % 6]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-500">No traffic data</div>
                )}
              </div>
              
              <div>
                <h5 className="font-medium text-gray-800 mb-3">Distribution by Source</h5>
                <ul className="space-y-4">
                  {trafficData.map((source) => (
                    <li key={source.source} className="flex items-center">
                      <span className="w-32 text-sm text-gray-700">{source.source}</span>
                      <div className="flex-1 h-4 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 rounded-full" 
                          style={{ width: `${source.percentage}%` }}
                        ></div>
                      </div>
                      <span className="ml-3 text-sm font-medium text-gray-900">{source.percentage}%</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="mt-6">
              <h5 className="font-medium text-gray-800 mb-3">Traffic Insights</h5>
              <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
                <p className="text-sm text-blue-800">
                  <strong>Insight:</strong> The majority of your affiliate traffic (35%) comes from direct referrals. 
                  Consider creating more blog content to increase traffic from that channel.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsAnalytics;
